import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { Banknote, CreditCard, CheckCircle2, Loader2 } from 'lucide-react';
import Button from '../../ui/Button';
import { Dialog } from '../accounts/AccountBits';
import nurseryCounterService from '../../../services/nurseryCounter';
import { errorMessage } from '../../../utils/apiResponse';
import { money, round2 } from '../../../utils/nurseryMoney';
import { parseAmount, quickTenders } from '../../../utils/counterCash';
import { formatDateTimeInTz } from '../../../utils/timezone';
import type { CounterPaymentState, OpenTill } from '../../../types/nurseryCounter';

/**
 * Taking payment for a walk-in sale, on its order page.
 *
 * Card: a QR code of a Stripe payment page on the nursery's own Stripe
 * account; the customer pays on their phone and this card says so the moment
 * Stripe does. Cash: into an open till, with what was handed over and the
 * change worked out and kept.
 *
 * What is due is the invoice total, GST included: the server works it out the
 * way the invoice will, so a sale picked in full invoices at what was paid.
 */

/** How often the card dialog asks Stripe whether the customer has paid. */
const CARD_POLL_MS = 3000;

export interface CounterPaymentCardProps {
  orderId: number;
  timezone: string;
  /** Called once payment is taken, so the page reloads the order. */
  onPaid: () => void;
}

export function CounterPaymentCard({ orderId, timezone, onPaid }: CounterPaymentCardProps) {
  const { t } = useTranslation('nursery', { keyPrefix: 'CounterPaymentCard' });
  const [state, setState] = useState<CounterPaymentState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dialog, setDialog] = useState<'card' | 'cash' | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await nurseryCounterService.getPayment(orderId);
      if (res.success) setState(res.data);
      else setError(errorMessage(res, t('readFailed')));
    } catch (e) {
      setError(errorMessage(e, t('readFailed')));
    }
  }, [orderId, t]);

  useEffect(() => { load(); }, [load]);

  const startCard = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await nurseryCounterService.startCard(orderId);
      if (res.success) {
        setState(res.data);
        setDialog('card');
      } else setError(errorMessage(res, t('startFailed')));
    } catch (e) {
      setError(errorMessage(e, t('startFailed')));
    }
    setBusy(false);
  };

  const paidNow = (next: CounterPaymentState) => {
    setState(next);
    setDialog(null);
    onPaid();
  };

  if (!state) {
    return error ? (
      <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700 break-words">{error}</div>
    ) : null;
  }

  const p = state.payment;
  const paid = p.status === 'paid';

  return (
    <section className="bg-white border border-neutral-200 rounded-xl p-4 space-y-3" aria-label={t('title')}>
      <h2 className="text-sm font-semibold text-neutral-600 uppercase tracking-wide">{t('title')}</h2>

      {paid ? (
        <div className="flex items-start gap-2 text-sm text-green-900 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
          <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" />
          <div className="min-w-0 break-words">
            <p className="font-medium">
              {p.paid_at
                ? t(p.method === 'cash' ? 'paidCashOn' : 'paidCardOn', { amount: p.paid_amount != null ? money(p.paid_amount) : '', when: formatDateTimeInTz(p.paid_at, timezone) })
                : t(p.method === 'cash' ? 'paidCash' : 'paidCard', { amount: p.paid_amount != null ? money(p.paid_amount) : '' })}
            </p>
            {p.cash && (
              <p>
                {t('into', { till: p.cash.till_name })}
                {p.cash.tendered != null ? t('intoHanded', { amount: money(p.cash.tendered) }) : ''}
                {p.cash.change_given ? t('intoChange', { amount: money(p.cash.change_given) }) : ''}.
              </p>
            )}
          </div>
        </div>
      ) : (
        <>
          {state.amount_due != null && (
            <p className="text-sm text-neutral-700">
              <Trans t={t} i18nKey="due" values={{ amount: money(state.amount_due) }}
                components={{ amount: <span className="text-2xl font-semibold text-neutral-900" /> }} />
            </p>
          )}
          {state.blockers.length > 0 && (
            <ul className="text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 space-y-1 break-words">
              {state.blockers.map((b) => <li key={b}>{b}</li>)}
            </ul>
          )}
          {error && <p className="text-sm text-red-700 break-words" role="alert">{error}</p>}
          {state.can_take && (
            <div className="flex flex-wrap gap-2">
              <Button onClick={startCard} disabled={busy || !state.card_payments.ready}>
                <CreditCard className="h-4 w-4" /> {state.card ? t('showQr') : t('card')}
              </Button>
              <Button variant="outline" onClick={() => { setError(null); setDialog('cash'); }} disabled={busy}>
                <Banknote className="h-4 w-4" /> {t('cash')}
              </Button>
            </div>
          )}
          {state.can_take && !state.card_payments.ready && (
            <p className="text-xs text-neutral-500 break-words">
              {!state.card_payments.configured
                ? t('notConfigured')
                : state.card_payments.connected
                  ? <Trans t={t} i18nKey="notReady" components={{ link: <Link to="/accounts/stripe" className="underline" /> }} />
                  : <Trans t={t} i18nKey="connect" components={{ link: <Link to="/accounts/stripe" className="underline" /> }} />}
            </p>
          )}
        </>
      )}

      {dialog === 'card' && state.card && (
        <CardPaymentDialog orderId={orderId} state={state} onPaid={paidNow}
          onUpdate={setState} onClose={() => { setDialog(null); load(); }} />
      )}
      {dialog === 'cash' && state.amount_due != null && (
        <CashPaymentDialog orderId={orderId} amountDue={state.amount_due} tills={state.open_tills}
          onPaid={paidNow} onClose={() => setDialog(null)} />
      )}
    </section>
  );
}

// ── Card ────────────────────────────────────────────────────────────────────

function CardPaymentDialog({ orderId, state, onPaid, onUpdate, onClose }: {
  orderId: number;
  state: CounterPaymentState;
  onPaid: (s: CounterPaymentState) => void;
  onUpdate: (s: CounterPaymentState) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation('nursery', { keyPrefix: 'CounterPaymentCard' });
  const [error, setError] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const card = state.card;

  // Ask Stripe every few seconds until the customer has paid.
  useEffect(() => {
    let stopped = false;
    const tick = async () => {
      try {
        const res = await nurseryCounterService.checkCard(orderId);
        if (stopped || !res.success) return;
        if (res.data.payment.status === 'paid') {
          onPaid(res.data);
        } else if (!res.data.card) {
          setError(t('cardExpired'));
          onUpdate(res.data);
        }
      } catch {
        // No signal for a moment: the next tick asks again.
      }
    };
    const timer = setInterval(tick, CARD_POLL_MS);
    return () => { stopped = true; clearInterval(timer); };
  }, [orderId, onPaid, onUpdate, t]);

  const cancel = async () => {
    setCancelling(true);
    setError(null);
    try {
      const res = await nurseryCounterService.cancelCard(orderId);
      if (res.success) {
        onUpdate(res.data);
        onClose();
        return;
      }
      setError(errorMessage(res, t('cancelFailed')));
    } catch (e) {
      setError(errorMessage(e, t('cancelFailed')));
    }
    setCancelling(false);
  };

  return (
    <Dialog
      title={t('cardTitle', { amount: card ? money(card.amount) : '' })}
      subtitle={t('cardSubtitle')}
      onClose={onClose}
      footer={
        <>
          <Button variant="outline" onClick={cancel} disabled={cancelling}>
            {cancelling ? t('cancelling') : t('cancelCard')}
          </Button>
          <Button variant="outline" onClick={onClose}>{t('keepWaiting')}</Button>
        </>
      }
    >
      {card?.qr ? (
        <img src={card.qr} alt={t('qrAlt')} className="mx-auto w-64 h-64 max-w-full" />
      ) : (
        <p className="text-sm text-neutral-600">{t('noQr')}</p>
      )}
      {card?.stale && (
        <p className="text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 break-words">
          {state.amount_due != null ? t('staleAmount', { amount: money(state.amount_due) }) : t('staleRight')}
        </p>
      )}
      {card?.url && (
        <p className="text-xs text-neutral-500 break-all">
          <Trans t={t} i18nKey="orOpen" components={{ link: <a href={card.url} target="_blank" rel="noreferrer" className="underline" /> }} />
        </p>
      )}
      <p className="flex items-center gap-2 text-sm text-neutral-700" role="status">
        <Loader2 className="h-4 w-4 animate-spin" /> {t('waiting')}
      </p>
      {error && <p className="text-sm text-red-700 break-words" role="alert">{error}</p>}
    </Dialog>
  );
}

// ── Cash ────────────────────────────────────────────────────────────────────

function CashPaymentDialog({ orderId, amountDue, tills, onPaid, onClose }: {
  orderId: number;
  amountDue: number;
  tills: OpenTill[];
  onPaid: (s: CounterPaymentState) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation('nursery', { keyPrefix: 'CounterPaymentCard' });
  const [sessionId, setSessionId] = useState<number | ''>(tills.length === 1 ? tills[0].id : '');
  const [tendered, setTendered] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<CounterPaymentState | null>(null);
  const [part, setPart] = useState(false);

  const handed = parseAmount(tendered) ?? 0;
  const change = round2(handed - amountDue);
  const enough = parseAmount(tendered) != null && handed + 0.004 >= amountDue;

  const take = async () => {
    if (!sessionId) return;
    setBusy(true);
    setError(null);
    try {
      const res = await nurseryCounterService.takeCash(orderId, sessionId, handed, !enough && part);
      if (res.success) {
        setResult(res.data);
        setBusy(false);
        return;
      }
      setError(errorMessage(res, t('cashFailed')));
    } catch (e) {
      setError(errorMessage(e, t('cashFailed')));
    }
    setBusy(false);
  };

  if (result) {
    const given = result.change_given ?? 0;
    return (
      <Dialog title={t('cashTaken')} onClose={() => onPaid(result)}
        footer={<Button onClick={() => onPaid(result)}>{t('done')}</Button>}>
        <p className="text-sm text-neutral-700 break-words">
          {handed + 0.004 < amountDue
            ? t('shortAccepted', { amount: money(handed), short: money(round2(amountDue - handed)) })
            : t('paidInCash', { amount: money(amountDue) })}
        </p>
        <p className="text-3xl font-semibold text-neutral-900" role="status">
          {given > 0 ? t('giveChange', { amount: money(given) }) : t('noChangeToGive')}
        </p>
      </Dialog>
    );
  }

  return (
    <Dialog
      title={t('takeTitle', { amount: money(amountDue) })}
      onClose={onClose}
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={busy}>{t('cancel')}</Button>
          <Button onClick={take} disabled={!sessionId || !(enough || (part && handed > 0)) || busy}>{busy ? t('recording') : t('takeCash')}</Button>
        </>
      }
    >
      {tills.length === 0 ? (
        <p className="text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 break-words">
          <Trans t={t} i18nKey="noTill" components={{ link: <Link to="/nursery/tills" className="underline font-medium" /> }} />
        </p>
      ) : (
        <div>
          <label htmlFor="cash-till" className="block text-sm font-medium text-neutral-700 mb-1">{t('till')}</label>
          <select id="cash-till" value={sessionId} onChange={(e) => setSessionId(e.target.value ? Number(e.target.value) : '')}
            className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm">
            {tills.length > 1 && <option value="">{t('chooseTill')}</option>}
            {tills.map((till) => <option key={till.id} value={till.id}>{till.till_name}</option>)}
          </select>
        </div>
      )}
      <div>
        <label htmlFor="cash-tendered" className="block text-sm font-medium text-neutral-700 mb-1">{t('handedOver')}</label>
        <input id="cash-tendered" type="text" inputMode="decimal" value={tendered}
          onChange={(e) => setTendered(e.target.value.replace(/[^\d.]/g, ''))}
          className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-lg" placeholder={amountDue.toFixed(2)} />
        <div className="flex flex-wrap gap-2 mt-2">
          {quickTenders(amountDue).map((v) => (
            <button key={v} type="button" onClick={() => setTendered(v.toFixed(2))}
              className="px-3 py-1.5 rounded-lg border border-neutral-300 text-sm hover:bg-neutral-50">
              {money(v)}
            </button>
          ))}
        </div>
      </div>
      {tendered.trim() !== '' && (
        <p className={`text-lg font-semibold ${enough ? 'text-neutral-900' : 'text-red-700'}`} role="status">
          {enough ? (change > 0 ? t('change', { amount: money(change) }) : t('noChange')) : t('short', { amount: money(round2(amountDue - handed)) })}
        </p>
      )}
      {tendered.trim() !== '' && !enough && handed > 0 && (
        <label className="flex items-start gap-2 text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 cursor-pointer break-words">
          <input type="checkbox" className="mt-0.5" checked={part} onChange={(e) => setPart(e.target.checked)} />
          <span className="min-w-0">{t('acceptShort', { amount: money(handed), short: money(round2(amountDue - handed)) })}</span>
        </label>
      )}
      {error && <p className="text-sm text-red-700 break-words" role="alert">{error}</p>}
    </Dialog>
  );
}
